import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Circle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageMeta from "@/components/PageMeta";

const AISLES = { produce: "Fresh Produce", meat: "Meat & Fish", dairy: "Dairy & Eggs", bakery: "Bakery", frozen: "Frozen",
  baking: "Baking Cupboard", spices: "Spices & Seasoning", pantry: "Pantry", drinks: "Drinks", other: "Other" };
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const monday = () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); };

function GuestList({ token, role }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem("rf_guest_name") || "");
  const [add, setAdd] = useState("");
  const headers = { "X-Share-Token": token, "X-Guest-Name": name || "Guest" };
  const load = () => api.get("/shopping-list", { headers }).then(({ data }) => setItems(data.items || [])).catch(() => setItems((v) => v || []));
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => {
    const g = {};
    (items || []).forEach((i) => { (g[i.aisle || "other"] ||= []).push(i); });
    return Object.entries(g);
  }, [items]);
  const needName = role === "edit" && !name;
  const tick = async (i) => {
    if (needName) return toast("Add your name first so others know who ticked it");
    try { await api.patch(`/shopping-list/${i.item_id}`, { checked: !i.checked }, { headers }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  const addItem = async (e) => {
    e.preventDefault();
    if (!add.trim()) return;
    try { await api.post("/shopping-list/add-manual", { name: add.trim() }, { headers }); setAdd(""); load(); } catch (e2) { toast.error(e2?.response?.data?.detail || "Couldn't add"); }
  };
  if (items === null) return <p className="text-rf-ink-soft">Loading…</p>;
  return (
    <div className="space-y-6">
      {role === "edit" && (
        <form onSubmit={(e) => { e.preventDefault(); const v = e.target.elements.n.value.trim(); if (v) { localStorage.setItem("rf_guest_name", v); setName(v); } }}
              className={`flex gap-2 ${name ? "hidden" : ""}`} data-testid="guest-name-form">
          <input name="n" placeholder="Your name (shown on your changes)" className="input-rf flex-1" data-testid="guest-name-input" />
          <button className="btn-ink !px-5" data-testid="guest-name-save">Save</button>
        </form>
      )}
      {role === "edit" && name && (
        <form onSubmit={addItem} className="flex gap-2">
          <input value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Add an item" className="input-rf flex-1 !py-4 text-lg" data-testid="guest-add-input" />
          <button className="btn-amber !px-6" data-testid="guest-add-submit">Add</button>
        </form>
      )}
      {groups.length === 0 && <p className="text-rf-ink-soft" data-testid="guest-empty">This list is empty.</p>}
      {groups.map(([aisle, rows]) => (
        <section key={aisle}>
          <h2 className="text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-2">{AISLES[aisle] || aisle}</h2>
          <ul className="space-y-2">
            {rows.map((i) => (
              <li key={i.item_id}>
                <button type="button" onClick={() => role === "edit" && tick(i)} disabled={role !== "edit"} data-testid={`guest-item-${i.item_id}`}
                        className="w-full flex items-center gap-4 min-h-[56px] px-4 rounded-2xl bg-white border border-rf-ink/10 text-left text-lg">
                  {i.checked ? <CheckCircle size={28} weight="fill" className="text-emerald-600 shrink-0" /> : <Circle size={28} className="text-rf-ink/30 shrink-0" />}
                  <span className={`flex-1 ${i.checked ? "line-through text-rf-ink-soft" : "text-rf-ink"}`}>{i.display_name}{i.quantity ? ` · ${i.quantity} ${i.unit || ""}` : ""}</span>
                  {(i.edited_by || i.added_by) && <span className="text-xs text-rf-ink-soft">{i.edited_by || i.added_by}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function GuestPlan({ token }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    const load = () => api.get("/meal-plan", { params: { week_start: monday() }, headers: { "X-Share-Token": token } }).then(({ data }) => setPlan(data)).catch(() => setPlan((p) => p || { slots: [] }));
    load(); const id = setInterval(load, 10000); return () => clearInterval(id);
  }, [token]);
  if (!plan) return <p className="text-rf-ink-soft">Loading…</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="guest-plan">
      {DAYS.map((d) => (
        <div key={d} className="rounded-2xl bg-white border border-rf-ink/10 p-4">
          <p className="font-black capitalize mb-2">{d}</p>
          {(plan.slots || []).filter((s) => s.day === d && (s.recipe_title || s.note)).map((s) => (
            <p key={s.meal_type} className="text-sm"><span className="text-rf-ink-soft capitalize">{s.meal_type}:</span> {s.recipe_title || s.note}</p>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function GuestShare() {
  const { token } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/share-links/${token}`).then(({ data }) => setInfo(data)).catch(() => setErr(true)); }, [token]);
  useEffect(() => {
    if (loading || !user || !info) return;
    api.post("/shares/claim", { token }).then(({ data }) => {
      toast.success("Saved to your account");
      nav(data.resource_type === "meal_plan" ? `/planner?plan=${data.resource_id}` : `/shopping?list=${data.resource_id}`);
    }).catch(() => {});
  }, [user, loading, info]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="guest-share-page">
      <PageMeta title="Shared with you — Recipe Filer" noindex />
      {err ? (
        <div className="text-center py-20" data-testid="guest-share-invalid">
          <h1 className="font-display text-4xl font-black text-rf-ink mb-3">This link isn&apos;t valid any more</h1>
          <p className="text-rf-ink-soft">Ask the person who shared it to send a new one.</p>
        </div>
      ) : !info ? <p className="text-rf-ink-soft">Loading…</p> : (
        <>
          <p className="overline mb-1">{info.owner_name} shared this with you · {info.role === "edit" ? "you can edit" : "view only"}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-rf-ink mb-4" data-testid="guest-share-title">{info.name}</h1>
          <div className="rounded-2xl bg-rf-cream-alt p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="guest-save-cta">
            <p className="text-sm flex-1">Save this {info.resource_type === "meal_plan" ? "meal plan" : "list"} to your account — it&apos;s free, and you never need to pay to join.</p>
            <Link to={`/register?next=${encodeURIComponent(`/s/${token}`)}`} className="btn-amber !py-2 !px-4 text-sm" data-testid="guest-save-register">Save this list to your account</Link>
          </div>
          {info.resource_type === "meal_plan" ? <GuestPlan token={token} /> : <GuestList token={token} role={info.role} />}
        </>
      )}
    </div>
  );
}
